'use client';

import React, { useCallback, useEffect, useMemo } from 'react';
import { myVisitCounselApi } from '@/entities/planner/model/my-planner.api';
import type { VisitCounselView } from '@/entities/visit-counsel/model/visit-counsel.types';
import type { InquiryFlowSnapshot } from '@miso/design-system';
import type {
  RecordArrivalSiteRequest,
  MovingSiteRequest,
} from '@/entities/visit-counsel/model/intake.types';
import {
  arrivalAddressToFlowValues,
  flowResultToArrivalAddress,
} from '../model/buildArrivalAddressFlow';
import { createProgressFlowFetcher } from '@/shared/api/inquiry-flow/fetchProgressFlow';
import { ensureIntakeSessionStarted } from '../model/ensureIntakeSessionStarted';
import { StepFlow } from './StepFlow';

const FLOW_ID = 'progress-arrival-address';

interface Props {
  visitCounselId: string;
  counsel: VisitCounselView;
  showToast: (message: string, severity?: 'success' | 'error' | 'warning' | 'neutral') => void;
  refetch: () => Promise<{ counsel?: VisitCounselView | null } | undefined>;
  onAfterDone: (counsel: VisitCounselView) => void;
  onCancel: () => void;
  /** 이전 sub-stage 로부터 뒤로가기 진입 — 마지막 visible 질문에 위치. */
  startAtLast?: boolean;
  progressOffset?: number;
  progressTotal?: number;
  onStateChange?: (snap: InquiryFlowSnapshot) => void;
}

/**
 * 도착지 주소 step (Figma `222:13604` ~ `222:13669`).
 *
 * 도로명 주소 → 건물명 → 동 → 호/층 다단계 InquiryFlow. 저장 시
 * `recordArrivalSite` 호출 (현재 운반방식이 미저장 상태이므로 movingMethod 는 보내지
 * 않음 — 다음 sub-stage 에서 별도 입력).
 *
 * 도착지 평수(`arrival.supplyArea`) 는 Figma 시안에 별도 화면이 없어 이번 회차에서는
 * 출발지 평수(또는 1)를 fallback 으로 전송한다 — 옛 `useIntakeForm` 의 fallback
 * 로직과 동일.
 */
export function ArrivalAddressStepAdapter({
  visitCounselId,
  counsel,
  showToast,
  refetch,
  onAfterDone,
  onCancel,
  startAtLast,
  progressOffset,
  progressTotal,
  onStateChange,
}: Props) {
  // Firestore `playground_flows/progress-arrival-address` 의 FlowDocument 를 비동기 로드.
  const fetcher = useMemo(() => createProgressFlowFetcher(FLOW_ID), []);
  const initialValues = useMemo(
    () => arrivalAddressToFlowValues(counsel.arrivalAddress ?? null),
    [counsel.arrivalAddress]
  );

  // 뒤로가기 진입 — Q_HO / Q_FLOOR 는 switchTo variant 슬롯이며 InquiryFlow 는
  // primary 슬롯 id 만 매칭한다. 따라서 floor 가 저장된 경우에도 primary 인 'unit'
  // 으로 위치를 지정한다 (active variant 는 prefill 값 기반으로 자연스럽게 노출).
  const startAtQuestionId = startAtLast ? 'unit' : undefined;

  // 마운트 시점 intake-session 보장.
  useEffect(() => {
    void ensureIntakeSessionStarted(visitCounselId, counsel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleComplete = useCallback(
    async (values: Record<string, unknown>) => {
      const address = flowResultToArrivalAddress(values);
      if (!address) {
        showToast('도착지 주소를 입력해주세요.', 'warning');
        return;
      }

      const intakeArrival = counsel.sessions?.intake?.arrival;
      const supplyArea =
        (typeof counsel.arrivalSupplyArea === 'number' && counsel.arrivalSupplyArea > 0
          ? counsel.arrivalSupplyArea
          : null) ??
        (typeof intakeArrival?.supplyArea === 'number' && intakeArrival.supplyArea > 0
          ? intakeArrival.supplyArea
          : null) ??
        (typeof counsel.supplyArea === 'number' && counsel.supplyArea > 0
          ? counsel.supplyArea
          : null) ??
        1;

      const arrival: MovingSiteRequest = {
        supplyArea,
        address,
        // 운반방식/엘리베이터는 다음 sub-stage 에서 별도 저장.
        hasElevator: counsel.arrivalHasElevator ?? null,
        movingMethod: counsel.arrivalMovingMethod
          ? {
              type: counsel.arrivalMovingMethod.type,
              attributes: counsel.arrivalMovingMethod.attributes ?? null,
            }
          : null,
      };
      const request: RecordArrivalSiteRequest = { arrival };

      try {
        await ensureIntakeSessionStarted(visitCounselId, counsel);
        await myVisitCounselApi.recordArrivalSite(visitCounselId, request);
        showToast('도착지 주소가 저장되었습니다.', 'success');
        const refetched = await refetch();
        const updated = refetched?.counsel ?? null;
        if (updated) onAfterDone(updated);
        else onCancel();
      } catch (err) {
        showToast(err instanceof Error ? err.message : '도착지 주소 저장에 실패했습니다.', 'error');
      }
    },
    [visitCounselId, counsel, showToast, refetch, onAfterDone, onCancel]
  );

  return (
    <StepFlow
      fetcher={fetcher}
      initialValues={initialValues}
      onComplete={handleComplete}
      onCancel={onCancel}
      onError={(err) => showToast(err.message ?? '플로우 오류가 발생했습니다.', 'error')}
      startAtQuestionId={startAtQuestionId}
      progressOffset={progressOffset}
      progressTotal={progressTotal}
      onStateChange={onStateChange}
    />
  );
}
